import { clsx } from "clsx";
import { Loader2 } from "lucide-react";

interface SpinnerProps {
  size?: "sm" | "md" | "lg";
  className?: string;
}

export default function Spinner({ size = "md", className }: SpinnerProps) {
  const sizes = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8",
  };

  return (
    <Loader2
      className={clsx(
        "animate-spin text-accent-primary",
        sizes[size],
        className,
      )}
    />
  );
}

interface LoadingProps {
  message?: string;
  fullScreen?: boolean;
}

export function Loading({
  message = "Carregando...",
  fullScreen,
}: LoadingProps) {
  const content = (
    <div className="flex flex-col items-center justify-center gap-4">
      <Spinner size="lg" />
      <p className="text-text-secondary text-sm">{message}</p>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-bg-primary/80 backdrop-blur-sm flex items-center justify-center z-50">
        {content}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-12">{content}</div>
  );
}

interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className }: SkeletonProps) {
  return (
    <div className={clsx("animate-pulse bg-white/5 rounded-lg", className)} />
  );
}

export function TableSkeleton({
  rows = 5,
  cols = 4,
}: {
  rows?: number;
  cols?: number;
}) {
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex gap-4 p-4 bg-white/5 rounded-t-xl">
        {Array(cols)
          .fill(0)
          .map((_, i) => (
            <Skeleton key={i} className="h-4 flex-1" />
          ))}
      </div>

      {/* Rows */}
      {Array(rows)
        .fill(0)
        .map((_, i) => (
          <div key={i} className="flex gap-4 p-4 border-b border-white/5">
            {Array(cols)
              .fill(0)
              .map((_, j) => (
                <Skeleton key={j} className="h-4 flex-1" />
              ))}
          </div>
        ))}
    </div>
  );
}

export function CardSkeleton() {
  return (
    <div className="glass-card space-y-4">
      <Skeleton className="h-6 w-1/2" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/3" />
    </div>
  );
}
